import { EngineClass } from '../systems/ClassRegistry.js';
import { Resource } from '../utils/ResourceManager.js';
import { property } from '../utils/serialization/decorator.js';
import { parseJson } from '../utils/Utils.js';

import type {
  AnimationClipOptions,
  AnimationEventMarker,
  AnimationLayerDefinition,
  AnimationLayerMask,
  AnimationState,
  AnimationStateMachineConfig,
  ExternalAnimationSource,
  ParameterMap,
} from './AnimationTypes.js';

export const ANIMATION_CONFIG_LEGACY_EXTENSION = '.anim.json';
export const ANIMATION_CONFIG_RESOURCE_PREFIX = 'animconfig';
export const ANIMATION_CONFIG_RESOURCE_EXTENSION = `.${ANIMATION_CONFIG_RESOURCE_PREFIX}.json`;

export function isLegacyAnimationConfigPath(path: string): boolean {
  return path.toLowerCase().endsWith(ANIMATION_CONFIG_LEGACY_EXTENSION);
}

export function isAnimationConfigResourcePath(path: string): boolean {
  return path.toLowerCase().endsWith(ANIMATION_CONFIG_RESOURCE_EXTENSION);
}

@EngineClass('AnimationConfigResource', { isResource: true, resourcePrefix: ANIMATION_CONFIG_RESOURCE_PREFIX })
export class AnimationConfigResource extends Resource {
  @property()
  public autoPlay?: boolean;

  @property()
  public parameters: ParameterMap = {};

  @property()
  public states: Record<string, AnimationState> = {};

  @property({ required: true })
  public initialState: string = '';

  @property()
  public clipOptions: Record<string, AnimationClipOptions> = {};

  @property()
  public oneShotClips: string[] = [];

  @property()
  public masks: Record<string, AnimationLayerMask> = {};

  @property()
  public layer?: AnimationLayerDefinition;

  @property()
  public events: Record<string, AnimationEventMarker[]> = {};

  @property()
  public externalAnimationSources: Array<string | ExternalAnimationSource> = [];

  @property()
  public targetSkeletonProfile?: string;

  public toConfig(): AnimationStateMachineConfig {
    return {
      autoPlay: this.autoPlay,
      parameters: cloneJson(this.parameters),
      states: cloneJson(this.states),
      initialState: this.initialState,
      clipOptions: cloneJson(this.clipOptions),
      oneShotClips: cloneJson(this.oneShotClips),
      masks: cloneJson(this.masks),
      layer: cloneJson(this.layer),
      events: cloneJson(this.events),
      externalAnimationSources: cloneJson(this.externalAnimationSources),
      targetSkeletonProfile: this.targetSkeletonProfile,
    };
  }

  public static fromConfig(config: AnimationStateMachineConfig): AnimationConfigResource {
    const resource = new AnimationConfigResource();
    resource.autoPlay = config.autoPlay;
    resource.parameters = cloneJson(config.parameters) ?? {};
    resource.states = cloneJson(config.states) ?? {};
    resource.initialState = config.initialState ?? '';
    resource.clipOptions = cloneJson(config.clipOptions) ?? {};
    resource.oneShotClips = cloneJson(config.oneShotClips) ?? [];
    resource.masks = cloneJson(config.masks) ?? {};
    resource.layer = cloneJson(config.layer);
    resource.events = cloneJson(config.events) ?? {};
    resource.externalAnimationSources = cloneJson(config.externalAnimationSources) ?? [];
    resource.targetSkeletonProfile = config.targetSkeletonProfile;
    return resource;
  }
}

function cloneJson<T>(value: T): T {
  if (value === undefined || value === null) {
    return value;
  }
  return parseJson(JSON.stringify(value)) as T;
}
